/**
 * component.jsx
 * @author Arnaud PEZEL - Apyx (arnaud.pezel@me.com)
 * @description A Vue.js wrapper for OpenLayers
 * @created Thu Mar 22 2018 12:32:45 GMT+0100 (CET)
 * @last-modified Tue Mar 27 2018 09:04:30 GMT+0200 (CEST)
 */

import "ol/ol.css";
//import "~/styles/ol-overload.scss";
import { Vue, Component, Watch, Prop, Mixins } from "vue-property-decorator";
//import FileSaver from "file-saver";
import { debounce } from 'lodash';
import OlMap from "ol/Map";
import OlView from "ol/View";
import * as OlProj from "ol/proj";
import OlExtent from "ol/extent";
import * as OlControl from "ol/control";
import OlScaleLine from "ol/control/ScaleLine";
import OlAttribution from "ol/control/Attribution";
import OlGeomPoint from "ol/geom/Point";
import WithInteractionsMixin from "./with-interactions";
import GeoJSONMixin from "./with-draw-geojson";

const DEFAULT_PROJECTION = "EPSG:4326";
const DEFAULT_CENTER: [number, number] = [2.4521, 46.7562]; //WGS84
const DEFAULT_ZOOM = 8;
const DEFAULT_MIN_ZOOM = 2.55;
const DEFAULT_MAX_ZOOM = 18;
const DEBOUNCE_CENTER_CHANGE = 700;

// szing function
const NOOP = () => true;

/*
 * This class is intended to be as much generic as possible.
 * The behavior can be extended using mixins.
 */
@Component
export default class MapComponent extends Mixins(WithInteractionsMixin, GeoJSONMixin) {

  @Prop({required: true}) readonly layers!: any[];
  @Prop({default: () => DEFAULT_CENTER}) readonly center!: [number, number];
  @Prop({required: false}) readonly extent!: any[];
  @Prop({required: false}) readonly viewPadding!: any[];
  @Prop({default: DEFAULT_ZOOM}) readonly zoom!: number;
  @Prop({default: NOOP}) readonly sizer!: Function;

  protected map: any = null;
  protected handleCenterChangeDebounced: any = null;

  created() {
    this.handleCenterChangeDebounced = debounce(this.handleCenterChange, DEBOUNCE_CENTER_CHANGE);
  }

  @Watch("layers")
  layersChanged() {
    if (this.map) {
      // make a diff
      this.map
        .getLayers()
        .getArray()
        .forEach(lyr => {
          if (this.layers.indexOf(lyr) === -1) {
            this.map.removeLayer(lyr);
          }
        });
      this.layers.map(lyr => {
        if (
          this.map
            .getLayers()
            .getArray()
            .indexOf(lyr) === -1
        ) {
          this.map.addLayer(lyr);
        }
      });
      this.layers.forEach((lyr, i) => lyr.setZIndex(i));
    }
  }

  @Watch("center")
  centerChanged() {
    if (this.map && this.center) {
      if (this.viewPadding) {
        let size = this.map.getSize();
        this.map.getView().fit(
          this.pointFromLonLat(this.center),
          {
            size,
            padding: this.viewPadding,
            maxZoom: this.zoom
          }
        );
      } else {
        this.zoomAndCenterTo({center: this.center});
      }
    }
  }

  @Watch("extent")
  extentChanged() {
    if (this.map && this.extent) {
      this.map.getView()
        .fit(
          OlExtent.applyTransform(this.extent, OlProj.getTransform('EPSG:4326', 'EPSG:3857')),
          {
            size: this.map.getSize(),
            padding: this.viewPadding || [0, 0, 0, 0]
          }
        );
    }
  }

  @Watch("zoom")
  zoomChanged() {
    if (this.map && this.zoom !== this.map.getView().getZoom()) { 
      this.zoomAndCenterTo({zoom: this.zoom})
    }
  }

  render() {
    return (
      <div style="width: 100%; height: 100%" class="map-wrapper">
        <div style="width: 100%; height: 100%">
        </div>
        <div class="attrib-scale-line">
          <div id="ol-attrib"/>
          <div id="ol-scaleline"/>
        </div>
      </div>
    );
  }

  mounted() {
    this.initSizingListeners().then(() => {
      this.initMap();
      this.initEmitters();
      this.viewPadding && this.centerChanged();
      this.handleResize();
      this.$emit("ready");
    });
  }

  initMap() {
    const scaleLine = new OlScaleLine({
      target: document.getElementById("ol-scaleline")
    });
    const attributions = new OlAttribution({
      target: document.getElementById("ol-attrib"),
      collapsible: false
    });
    this.map = new OlMap({
      target: this.$el.children[0],
      layers: this.layers,
      view: new OlView({
        center: this.projLonLat(this.center || DEFAULT_CENTER),
        zoom: this.zoom || DEFAULT_ZOOM,
        minZoom: DEFAULT_MIN_ZOOM,
        maxZoom: DEFAULT_MAX_ZOOM,
        projection: DEFAULT_PROJECTION
      }),
      controls: OlControl.defaults().extend([scaleLine, attributions])
    });
    this.extentChanged();
  }

  initSizingListeners() {
    window.addEventListener("resize", () => this.handleResize());
    return this.handleResize();
  }

  initEmitters() {
    this.map.on("click", e => this.handlePointerClick(e));
    this.map.on("pointermove", e => this.handlePointerMove(e));
    this.map.getView().on("change:resolution", e => this.handleResolutionChange(e));
    this.map.getView().on('propertychange', e => this.handleMapPropertyChange(e));
  }

  handlePointerClick(e) {
    const coordinate = this.map.getEventCoordinate(e.originalEvent);
    this.$emit("mapClick", {
      coordinate,
      wgs84Coordinate: this.reverseProjLonLat(coordinate),
      pixel: e.pixel,
      target: this,
      features: this.getFeaturesAtPixel(e.pixel)
    });
  }

  handlePointerMove(e) {
    if (e.dragging) return;
    const coordinate = this.map.getEventCoordinate(e.originalEvent);
    this.$emit("mapMouseMove", {
      coordinate,
      wgs84Coordinate: this.reverseProjLonLat(coordinate),
      pixel: e.pixel,
      target: this,
      features: this.getFeaturesAtPixel(e.pixel)
    });
  }

  handleMapPropertyChange(e) {
    switch (e.key) {
      case 'center':
        this.handleCenterChangeDebounced(e);
        break;
        case 'resolution':
        this.handleCenterChangeDebounced(e);
        break;
    }
  }

  handleCenterChange(e) {
    this.$emit("center-changed", this.getCenterAndZoom(), e, this);
  }

  handleResolutionChange(e) {
    this.$emit("resolution-changed", {...this.getCenterAndZoom(), event: e, target: this});
  }

  utfGridGetData(_) {
    return new Promise(res => {
      //not implemented
      res(null);
    });
  }

  handleResize() {
      return Promise.resolve(this.sizer.call(this, this.$el.children[0]))
          .then(() => this.map && this.map.updateSize());
  }

  /* UTILS */

  extentAsRing(ext) {
    return [
      [ext[0], ext[1]], [ext[2], ext[1]], [ext[2], ext[3]],
      [ext[0], ext[3]], [ext[0], ext[1]]
    ];
  }

  mapProjection() {
    return (this.map && this.map.getView().getProjection()) || DEFAULT_PROJECTION;
  }

  projLonLat(lonLat: [number, number]) {
    return OlProj.transform(lonLat, "EPSG:4326", this.mapProjection());
  }
  
  reverseProjLonLat(lonLat) {
    return OlProj.transform(lonLat, this.mapProjection(), "EPSG:4326");
  }

  /* PUBLIC METHODS */

  getExtentRing(padding) {
    let ext = this.map.getView().calculateExtent(this.map.getSize().map( s => s+padding ));
    return this.extentAsRing(ext);
  }

  getExtentGeoJSON(padding) {
    return {
        "type": "Polygon",
        "coordinates": [this.getExtentRing(padding)]
    };
  }

  getFeaturesAtPixel(pixel) {
    return this.map.getFeaturesAtPixel(pixel);
  }

  getCenterAndZoom() {
    return {    
      center: OlProj.toLonLat(this.map.getView().getCenter()),
      centerWgs84: OlProj.transform(
          this.map.getView().getCenter(),
          this.map.getView().getProjection(),
          "EPSG:4326"
      ), 
      resolution: this.map.getView().getResolution(),
      metersPerUnit: this.mapProjection().getMetersPerUnit(),
      zoom: this.map.getView().getZoom()
    }
  }

  zoomAndCenterTo({zoom, center}: {zoom?: number, center?: [number, number]}) {
    if (center) {
      this.map.getView().setCenter(this.projLonLat(center));
    }
    if (zoom != null) {
      this.map.getView().setZoom(zoom);
    }
  }

  /**
   * take coords and return an ol point
   * in view projection system
   */
  pointFromLonLat(coords) {
    return new OlGeomPoint(
      this.projLonLat(coords)
    );
  }

}
// VUE JSX HOT LOADER //
if (module.hot) require("/Users/home/j2ee/projects/cavites/src/main/client/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: eval('this'), module: module, hotId: "_vue_jsx_hot-0c89e15a/index.tsx" });